import { useEffect, useMemo, useRef, useState } from 'react';
import { uploadCanvasImage } from '../services/api';
import { CartError, addToCart, goToCheckout } from '../services/shopify-cart';
import type { CanvasMountData, ShopifyVariant } from '../types';
import { shopifyImg } from '../utils/img';
import { normalizeImageFile } from '../utils/heic';

interface Props {
  data: CanvasMountData;
}

const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

type CheckoutPhase = 'idle' | 'submitting' | 'redirecting';

/**
 * TEMP fallback — shown only when the Canvas product has no `ui_config.moments`.
 * Verbatim from fixtures/beyond/bf-renaissance.ui_config.json (same review images
 * the UI demo used); only the mojibake `�` chars were fixed. Replace by
 * setting `moments` on the product's `custom.ui_config` metafield.
 */
const DEFAULT_MOMENTS: Array<{ image: string; name: string; review: string }> = [
  {
    image:
      'https://cdn.shopify.com/s/files/1/0804/8931/6579/files/bf-renaissance-review-1.webp?v=1782308756',
    name: 'Megan',
    review:
      "We purchased this personalized pet and owner portrait for our son-in-law after the unexpected loss of his beloved dog. I received a proof now, and a weeks later, the original painting arrived — absolutely beautiful and flawless. There were no issues at all throughout the process. The gift was incredibly well received and very meaningful. I actually searched for something like this because someone gave me a similar portrait when my own dog passed, and it's something I truly treasure. This is such a thoughtful, personal gift idea. Thank you so much!",
  },
  {
    image:
      'https://cdn.shopify.com/s/files/1/0804/8931/6579/files/bf-renaissance-review-2.webp?v=1782308756',
    name: 'Ashley',
    review:
      "Easily one of my favorite gifts for a friend. Her customer service was amazing. The only regret I have is purchasing the digital and not getting the canvas because it was a last minute gift. If you're just thinking about getting one, just go for it and order your portrait!!!",
  },
  {
    image:
      'https://cdn.shopify.com/s/files/1/0804/8931/6579/files/bf-renaissance-review-3.webp?v=1782308756',
    name: 'Brittany',
    review:
      'This site made this gift for myself so special! I got my favorite photo of me and my dog of 14 years that passed away. It definitely made me smile. The picture is adorable. Definitely would buy again from this seller!',
  },
];

/** Read `img_url`/`image_url` + `ref_order`/`order` from the current URL. */
function readUrlContext(): { imgUrl: string; refOrder: string } {
  if (typeof window === 'undefined') return { imgUrl: '', refOrder: '' };
  const p = new URLSearchParams(window.location.search);
  const imgUrl = (p.get('img_url') || p.get('image_url') || '').trim();
  const refOrder = (p.get('ref_order') || p.get('order') || '').trim();
  return { imgUrl, refOrder };
}

/**
 * CanvasUpsellApp — the Canvas product page (#bf-canvas-root).
 *
 * Faithful port of doc-31-8/canvas_upsell_ui_demo.html into the widget design
 * system. One product, two modes decided by the URL:
 *   • Mode 1 (context / upsell): `?img_url=…&ref_order=…` present → artwork
 *     auto-loaded, upload hidden, `ref_order` carried onto the cart line item
 *     (`_original_order_id`) for fulfilment traceability.
 *   • Mode 2 (no context / retail): buyer uploads their own image, hosted via
 *     POST /canvas-upload.
 *
 * Canvas variant prices are managed by the merchant in Shopify Admin (already
 * net of the digital credit) — there is no discount step.
 */
export function CanvasUpsellApp({ data }: Props) {
  const ctx = useMemo(() => readUrlContext(), []);
  const hasContext = ctx.imgUrl !== '';

  const currency = window.__BF_CONFIG__?.currency || 'USD';

  const variants = useMemo(
    () => data.variants.filter((v) => v.available !== false),
    [data.variants],
  );
  const [selectedId, setSelectedId] = useState<number | null>(variants[0]?.id ?? null);
  const selected = variants.find((v) => v.id === selectedId) ?? null;

  const [imageUrl, setImageUrl] = useState<string>(hasContext ? ctx.imgUrl : '');
  const [localPreview, setLocalPreview] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const fileRef = useRef<HTMLInputElement>(null);

  const [phase, setPhase] = useState<CheckoutPhase>('idle');
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  useEffect(
    () => () => {
      if (localPreview) URL.revokeObjectURL(localPreview);
    },
    [localPreview],
  );

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    e.target.value = '';
    if (!picked) return;
    setUploadError(null);
    if (!picked.type.startsWith('image/') && !/\.hei[cf]$/i.test(picked.name)) {
      setUploadError('Please choose an image file (JPG or PNG).');
      return;
    }
    let file: File;
    try {
      file = await normalizeImageFile(picked);
    } catch {
      setUploadError('Could not read that image. Please try a different file.');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadError('That file is larger than 50 MB. Please choose a smaller export.');
      return;
    }
    const preview = URL.createObjectURL(file);
    setLocalPreview(preview);
    setFileName(file.name);
    setImageUrl('');
    setUploading(true);
    try {
      const { url } = await uploadCanvasImage(file);
      setImageUrl(url);
    } catch {
      setUploadError('Upload failed. Please check your connection and try again.');
      setLocalPreview('');
      setFileName('');
      URL.revokeObjectURL(preview);
    } finally {
      setUploading(false);
    }
  };

  const displayImage = imageUrl || localPreview;
  const ready = !!imageUrl && !uploading;
  const canOrder = !!selected && ready && phase === 'idle';

  const handleOrder = async () => {
    if (!selected || !imageUrl || phase !== 'idle') return;
    setPhase('submitting');
    setCheckoutError(null);
    try {
      await addToCart([
        {
          variantId: selected.id,
          properties: {
            _ai_image_url: imageUrl,
            _ai_image_url_highres: imageUrl,
            _print_source: hasContext ? 'upsell' : 'retail',
            ...(ctx.refOrder ? { _original_order_id: ctx.refOrder } : {}),
          },
        },
      ]);
      setPhase('redirecting');
      goToCheckout();
    } catch (err) {
      const msg =
        err instanceof CartError
          ? err.shopifyDescription || err.message
          : err instanceof Error
            ? err.message
            : 'Could not start checkout. Please try again.';
      setCheckoutError(msg);
      setPhase('idle');
    }
  };

  // Fall back to the temporary placeholder reviews when the product has none.
  const moments = data.moments.length > 0 ? data.moments : DEFAULT_MOMENTS;
  const showcase = useMemo(
    () =>
      moments
        .map((m) => m.image)
        .filter((s): s is string => typeof s === 'string' && s.length > 0)
        .slice(0, 3),
    [moments],
  );
  const reviews = useMemo(() => moments.filter((m) => m && m.review).slice(0, 3), [moments]);

  const ctaLabel =
    phase === 'redirecting'
      ? 'Taking you to checkout…'
      : phase === 'submitting'
        ? 'Processing…'
        : uploading
          ? 'Uploading…'
          : ready
            ? 'Claim Your Canvas'
            : 'Upload to Get Started';

  return (
    <div className="w-full max-w-[672px] mx-auto flex flex-col" style={{ minWidth: 0 }}>
      {/* Main white card — the page title comes from the theme section hero. */}
      <div className="bg-white sm:rounded-xl sm:shadow-soft overflow-hidden p-5 sm:p-10">
        {/* Showcase gallery — 3-up */}
        {showcase.length > 0 && (
          <div
            className="mb-8"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10 }}
          >
            {showcase.map((src, i) => (
              <div key={i} className="relative rounded-lg overflow-hidden shadow-soft" style={{ aspectRatio: '4 / 5' }}>
                <img
                  src={shopifyImg(src, 500)}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full"
                  style={{ objectFit: 'cover', objectPosition: 'center' }}
                />
                <span
                  aria-hidden="true"
                  className="absolute inset-0"
                  style={{ background: 'linear-gradient(to top, rgba(74,64,54,0.2), transparent)' }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Order box */}
        <div
          className="rounded-xl overflow-hidden"
          style={{
            border: '2px solid rgba(168,118,62,0.55)',
            background: '#fdf6ee',
            boxShadow: '0 6px 32px rgba(160,113,68,0.18)',
          }}
        >
          <div className="p-5 sm:p-7">
            {/* Context section */}
            <div className="mb-5">
              {hasContext ? (
                <div className="flex items-start gap-3">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="text-teal flex-shrink-0"
                    style={{ marginTop: 1 }}
                    aria-hidden="true"
                  >
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-[11px] text-warm-brown/70 font-bold tracking-widest uppercase mb-1 m-0">
                      Auto-loaded artwork
                    </p>
                    <p className="text-sm text-warm-brown m-0">
                      We&rsquo;ve securely loaded your digital portrait
                      {ctx.refOrder ? (
                        <>
                          {' '}
                          from order <span className="font-bold">#{ctx.refOrder}</span>
                        </>
                      ) : null}
                      .
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <p className="font-semibold text-warm-brown/70 mb-3 text-[11px] tracking-wide uppercase m-0">
                    Start with your portrait
                  </p>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFile}
                  />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="w-full py-3.5 rounded-lg bg-teal text-white font-bold text-sm shadow-soft hover:bg-teal-dark disabled:opacity-60 transition-colors inline-flex items-center justify-center gap-2"
                  >
                    {uploading ? (
                      <Spinner label="Uploading…" />
                    ) : (
                      <>
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          aria-hidden="true"
                        >
                          <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        {imageUrl ? 'Upload a different photo' : 'Upload Your Portrait'}
                      </>
                    )}
                  </button>
                  <p className="text-center text-xs text-warm-brown/70 mt-3 leading-relaxed m-0">
                    <span className="font-semibold text-teal">💡 Tip:</span> Find your HD file in
                    the original email from Beyond Frames Studio or your saved Google Drive folder.
                    (JPG or PNG, max 50MB)
                  </p>
                  {fileName && !uploadError && (
                    <p className="text-sm font-bold text-green-700 mt-2 bg-green-50 p-2 rounded border border-green-200 m-0">
                      ✓ {fileName}
                    </p>
                  )}
                  {uploadError && (
                    <p className="text-xs text-red-600 text-center mt-2 m-0">{uploadError}</p>
                  )}
                </>
              )}
            </div>

            {/* Mockup area */}
            <div
              className="mt-5 rounded-xl overflow-hidden relative border border-soft-border flex items-center justify-center py-4"
              style={{ background: 'var(--color-cream2)' }}
            >
              <div
                aria-hidden="true"
                className="absolute inset-0"
                style={{
                  opacity: 0.2,
                  backgroundImage: 'radial-gradient(#a8763e 1px, transparent 1px)',
                  backgroundSize: '20px 20px',
                }}
              />
              {displayImage ? (
                <div className="relative z-10 flex flex-col items-center p-4">
                  <div
                    className="bg-white p-2 sm:p-3 border border-gray-100"
                    style={{ boxShadow: '0 15px 40px rgba(0,0,0,0.15)' }}
                  >
                    <img
                      src={shopifyImg(displayImage, 640)}
                      alt="Your artwork"
                      className="bf-portrait-img border border-gray-200"
                      style={{ width: 220, maxWidth: '100%', height: 'auto', objectFit: 'cover' }}
                    />
                  </div>
                  <div className="mt-4 inline-flex items-center gap-1.5 bg-white/80 px-3 py-1 rounded-full shadow-soft">
                    <span
                      aria-hidden="true"
                      className="bf-live-dot rounded-full"
                      style={{ width: 8, height: 8, background: '#22c55e' }}
                    />
                    <span className="text-warm-brown font-medium text-xs tracking-wide">
                      Ready for Print
                    </span>
                  </div>
                </div>
              ) : (
                <div className="relative z-10 p-4">
                  <div
                    className="bg-white/60 border-2 border-dashed rounded-lg flex flex-col items-center justify-center"
                    style={{ width: 128, height: 160, borderColor: 'rgba(168,118,62,0.4)' }}
                  >
                    <svg
                      width="38"
                      height="38"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="rgba(168,118,62,0.5)"
                      strokeWidth="1.5"
                      className="mb-2"
                      aria-hidden="true"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <circle cx="9" cy="9" r="2" />
                      <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
                    </svg>
                    <span className="text-gray-400 font-medium px-4 text-xs text-center">
                      Your Artwork
                      <br />
                      Will Appear Here
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Size */}
            <div className="mt-7 mb-6">
              <label className="text-[11px] font-semibold text-warm-brown/70 uppercase tracking-wide">
                Select Canvas Size
              </label>
              {variants.length === 0 ? (
                <p className="text-sm text-warm-brown/70 mt-3 m-0">
                  No canvas sizes are available right now.
                </p>
              ) : (
                <div
                  className="mt-3"
                  style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}
                >
                  {variants.map((v) => (
                    <SizeCard
                      key={v.id}
                      variant={v}
                      currency={currency}
                      selected={v.id === selectedId}
                      onSelect={() => setSelectedId(v.id)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* CTA */}
            <button
              type="button"
              onClick={handleOrder}
              disabled={!canOrder}
              className={[
                'w-full py-4 rounded-lg font-bold text-base uppercase tracking-wide transition-all inline-flex items-center justify-center gap-2',
                canOrder
                  ? 'bg-teal text-white shadow-soft hover:bg-teal-dark cursor-pointer'
                  : 'bg-cream2 text-warm-brown/55 border-2 border-soft-border cursor-not-allowed',
              ].join(' ')}
            >
              {phase !== 'idle' || uploading ? <Spinner label={ctaLabel} /> : ctaLabel}
            </button>

            {!ready && phase === 'idle' && !uploading && (
              <p className="text-center text-[13px] italic font-medium text-teal mt-2 flex items-center justify-center gap-1 m-0">
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Upload your artwork above to unlock this button.
              </p>
            )}
            {checkoutError && (
              <p className="text-sm text-red-600 text-center mt-2 m-0">{checkoutError}</p>
            )}

            {/* Trust signals */}
            <div className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-3 text-[11px] font-semibold text-warm-brown/70 uppercase tracking-wide">
              <span className="inline-flex items-center gap-1.5">
                <TrustIcon kind="ship" />
                Free Shipping
              </span>
              <span className="inline-flex items-center gap-1.5">
                <TrustIcon kind="shield" />
                Lifetime Color Guarantee
              </span>
              <span className="inline-flex items-center gap-1.5">
                <TrustIcon kind="clock" />
                Dispatch in 2-3 Days
              </span>
            </div>
          </div>
        </div>

        {/* Reviews — inside the white card, port of the demo's block */}
        {reviews.length > 0 && (
          <div className="mt-8 pt-8 border-t border-soft-border">
            <h3
              className="text-sm font-bold mb-6 uppercase tracking-widest text-center"
              style={{ color: '#4a4036' }}
            >
              What Our Customers Say
            </h3>
            <div className="flex flex-col gap-4">
              {reviews.map((m, i) => (
                <div
                  key={`${m.name}-${i}`}
                  className="bf-cv-review rounded-lg border"
                  style={{ background: 'rgba(243,238,232,0.5)', borderColor: 'rgba(226,220,211,0.6)' }}
                >
                  {m.image && (
                    <div className="bf-cv-review-media">
                      <img src={shopifyImg(m.image, 400)} alt="" loading="lazy" decoding="async" />
                    </div>
                  )}
                  <div className="bf-cv-review-text">
                    <Stars />
                    <p
                      className="italic leading-snug m-0"
                      style={{ color: '#6a5d4d', fontSize: 13, overflowWrap: 'anywhere' }}
                    >
                      &ldquo;{m.review}&rdquo;
                    </p>
                    {m.name && (
                      <p className="text-xs font-bold mt-2 m-0" style={{ color: '#4a4036' }}>
                        — {m.name}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stars() {
  return (
    <div className="flex mb-1.5" style={{ color: '#eab308' }} aria-hidden="true">
      {[0, 1, 2, 3, 4].map((n) => (
        <svg key={n} width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

// ─── Bits ───────────────────────────────────────────────────────────────────

function Spinner({ label }: { label: string }) {
  return (
    <>
      <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
        <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      </svg>
      {label}
    </>
  );
}

function TrustIcon({ kind }: { kind: 'ship' | 'shield' | 'clock' }) {
  const common = {
    width: 15,
    height: 15,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className: 'text-green-600',
    'aria-hidden': true,
  };
  if (kind === 'shield') {
    return (
      <svg {...common}>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="m9 12 2 2 4-4" />
      </svg>
    );
  }
  if (kind === 'clock') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 8v4l3 2" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8" />
    </svg>
  );
}

function SizeCard({
  variant,
  currency,
  selected,
  onSelect,
}: {
  variant: ShopifyVariant;
  currency: string;
  selected: boolean;
  onSelect: () => void;
}) {
  const label = variant.option2 || variant.option1;
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={[
        'relative rounded-lg p-4 border-2 text-center transition-all flex flex-col items-center justify-center gap-1 shadow-soft',
        selected
          ? 'border-teal bg-teal text-white bf-glow'
          : 'border-soft-border bg-white text-warm-brown/70 hover:border-teal',
      ].join(' ')}
    >
      {selected && (
        <span
          aria-hidden="true"
          className="absolute top-3 right-3 rounded-full bg-white flex items-center justify-center"
          style={{ width: 16, height: 16 }}
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#a8763e" strokeWidth="3">
            <path d="M5 13l4 4L19 7" />
          </svg>
        </span>
      )}
      <span className={['block', selected ? 'text-white font-bold' : 'font-semibold'].join(' ')} style={{ fontSize: 17 }}>
        {label}
      </span>
      <span className={selected ? 'text-white/80 text-sm' : 'text-warm-brown/60 text-sm'}>
        {formatMoney(variant.price, currency)}
      </span>
    </button>
  );
}

function formatMoney(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}
